<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container class="custom-container container candidate-profile-view">
        <b-container class="custom-container candidate-view-inner">
          <b-breadcrumb
            :items="breadcrumbs"
            class="pl-0 bg-transparent candidate-view-breadcrumb"
          ></b-breadcrumb>

          <div v-if="candidate" class="profile-header-row">
            <div class="profile-header-row__identity">
              <div class="profile-header-row__name-block">
                <h2 class="font-weight-bold mb-0">
                  {{ candidate.name || '-----' }}
                </h2>
                <span class="text-muted">{{ candidate.trade || '-' }}</span>
              </div>
              <favourite-toggle
                :tradie-id="candidate.id"
                :candidate="candidate"
                @error="onError"
              />
            </div>

            <b-button
              variant="outline-secondary"
              class="bg-white text-dark profile-header-row__back-btn"
              @click="goToCandidates()"
            >
              {{ backLabel }}
            </b-button>
          </div>

          <b-row v-if="candidate">
            <b-container fluid>
              <b-card
                class="mt-4 candidate-details"
                body-class="candidate-details__body"
              >
                <div class="candidate-details__header-row">
                  <div class="candidate-details__actions">
                    <resume-request-button
                      v-if="showResumeRequest"
                      :tradie-id="candidate.id"
                      :status="candidate.resume_request_status"
                      :retryable="candidate.resume_request_retryable"
                      @request="openResumeRequestModal"
                    />
                    <workforce-invite-button
                      :resume-request-status="candidate.resume_request_status"
                      :workforce-invitation-status="
                        candidate.workforce_invitation_status
                      "
                      :has-applied="!!candidate.has_applied"
                      @invite="openWorkforceInvitationModal"
                    />
                    <add-to-pool-dropdown
                      :tradie-id="candidate.id"
                      @error="onError"
                      @added="onAddedToPool"
                    />
                    <b-button
                      v-if="candidate.resume_available && candidate.resume_url"
                      variant="outline-primary"
                      size="sm"
                      :disabled="isLoadingResume"
                      class="d-inline-flex align-items-center justify-content-center"
                      @click="viewResume"
                    >
                      View Resume
                    </b-button>
                    <!-- Request was accepted (so a resume genuinely existed
                         and access was granted) but there's nothing to show
                         now — the tradie deleted it since. Without this, the
                         button above just silently disappears with no
                         explanation, which reads as a bug rather than
                         something the tradie did on their end. -->
                    <span
                      v-else-if="candidate.resume_request_status === 'accepted'"
                      class="candidate-details__resume-gone text-muted small"
                    >
                      <b-icon-exclamation-circle class="mr-1" />
                      This worker has since deleted their resume.
                    </span>
                  </div>
                </div>

                <candidate-profile-detail :candidate="candidate" />
              </b-card>
            </b-container>
          </b-row>
        </b-container>

        <resume-request-modal
          :showModal="isRequestingResume"
          :candidate="candidate"
          @closeModal="isRequestingResume = false"
          @sent="onResumeRequestSent"
        />

        <workforce-invitation-modal
          :showModal="isInvitingWorkforce"
          :candidate="candidate"
          @closeModal="isInvitingWorkforce = false"
          @sent="onWorkforceInvitationSent"
        />

        <FilePreviewModal
          v-if="resumePreviewUrl"
          :visible="!!resumePreviewUrl"
          :url="resumePreviewUrl"
          mime-type="application/pdf"
          name="Resume.pdf"
          :download-name="resumeDownloadName"
          @close="closeResumePreview"
        />

        <b-alert
          class="custom-alert"
          :show="dismissCountDown"
          :class="{ 'bg-danger border-danger': alertType === 'danger' }"
          dismissible
          fade
          :variant="alertType"
          @dismiss-count-down="(val) => (dismissCountDown = val)"
        >
          {{ alertMsg }}
        </b-alert>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import env from '@/env'
import CandidateProfileDetail from '@/components/candidates/CandidateProfileDetail.vue'
import ResumeRequestButton from '@/components/candidates/ResumeRequestButton.vue'
import WorkforceInviteButton from '@/components/candidates/WorkforceInviteButton.vue'
import FavouriteToggle from '@/components/candidates/FavouriteToggle.vue'
import AddToPoolDropdown from '@/components/candidates/AddToPoolDropdown.vue'
import ResumeRequestModal from '@/components/modals/ResumeRequestModal.vue'
import WorkforceInvitationModal from '@/components/modals/WorkforceInvitationModal.vue'
import FilePreviewModal from '@/components/modals/FilePreviewModal.vue'

export default {
  name: 'ViewCandidate',

  components: {
    CandidateProfileDetail,
    ResumeRequestButton,
    WorkforceInviteButton,
    FavouriteToggle,
    AddToPoolDropdown,
    ResumeRequestModal,
    WorkforceInvitationModal,
    FilePreviewModal,
  },

  // Record the exact route the user came from so "Back to Candidates"
  // returns there (In Progress, Pools, a pool detail, search results...)
  // instead of guessing with window.history.length + $router.go(-1), which
  // steps back one raw browser-history entry and can land on an unrelated
  // page or the wrong Candidates tab. beforeRouteUpdate covers navigating
  // straight from one candidate profile to another (guard is not re-run on
  // component reuse). fullPath keeps the origin list's filters/search/page.
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from && from.name && from.name !== 'candidates.view-candidate') {
        vm.originRoute = from.fullPath
      }
    })
  },

  beforeRouteUpdate(to, from, next) {
    if (from && from.name && from.name !== 'candidates.view-candidate') {
      this.originRoute = from.fullPath
    }
    next()
  },

  data() {
    return {
      candidateId: this.$route.params.candidateId,
      // Set by the route guards above; null on a direct link / hard reload.
      originRoute: null,
      isLoading: false,
      isLoadingResume: false,
      // The resume being previewed ('' when closed). resumeObjectUrl is set
      // when it is a local blob that must be released on close.
      resumePreviewUrl: '',
      resumeObjectUrl: null,
      isRequestingResume: false,
      isInvitingWorkforce: false,
      dismissCountDown: 0,
      alertType: 'primary',
      alertMsg: '',
    }
  },

  computed: {
    ...mapGetters('candidate', { candidate: 'currentCandidate' }),
    ...mapGetters('candidate', ['favourites']),

    // Opened from a job's applicants: Back returns to that job, so say so.
    backLabel() {
      return this.originRoute && this.originRoute.startsWith('/jobs')
        ? 'Back to Job'
        : 'Back to Candidates'
    },

    breadcrumbs() {
      return [
        { text: 'Candidates', to: '/candidates' },
        { text: this.candidate ? this.candidate.name : '' },
      ]
    },

    // candidate.resume_url is an authenticated API endpoint (Laravel's
    // route() returns it absolute already, e.g. http://tradie.api:8000/...),
    // not a public/signed link — it 401s if opened directly (a plain <a
    // target="_blank"> can't attach the Bearer token). Must be fetched
    // through the authenticated $api client instead; see viewResume().
    // "Jane Smith - Resume.pdf" for the preview's Download button.
    resumeDownloadName() {
      const name = this.candidate && this.candidate.name
      return name ? `${name} - Resume.pdf` : 'Resume.pdf'
    },

    // The resume is already viewable (they shared it with a job application),
    // so asking for it, or a red "Resume Declined", would only contradict the
    // View Resume button. An accepted request keeps its own green state.
    showResumeRequest() {
      const c = this.candidate
      if (!c) return false
      const viewable = !!(c.resume_available && c.resume_url)
      return !viewable || c.resume_request_status === 'accepted'
    },

    resumeRequestUrl() {
      if (!this.candidate || !this.candidate.resume_url) return null
      if (/^https?:\/\//i.test(this.candidate.resume_url)) {
        return this.candidate.resume_url
      }
      return `${env.apiUrl}${this.candidate.resume_url}`
    },
  },

  async mounted() {
    await this.fetchCandidate()
  },

  beforeDestroy() {
    this.releaseResumeObjectUrl()
  },

  methods: {
    ...mapActions('candidate', ['fetchCandidateById']),

    async fetchCandidate() {
      this.isLoading = true
      try {
        await this.fetchCandidateById(this.candidateId)
      } catch (err) {
        // CandidateProfileController::show() returns 409 (not 404) the one
        // time it excludes a tradie deliberately — already hired by this
        // company — rather than the profile genuinely being inaccessible.
        // Whatever sent us here (a stale candidate card, a notification for
        // an event that predates the hire, an old bookmark) had no way to
        // know that in advance; recover by sending them to the Tradies view
        // instead of showing a dead error on a page they can't use anymore.
        if (err && err.status === 409) {
          this.$router
            .replace({
              name: 'tradies.view-tradie',
              params: { tradieId: this.candidateId },
            })
            .catch(() => {})
          return
        }
        this.onError(err)
      } finally {
        this.isLoading = false
      }
    },

    // Return to the exact page the user came from (captured by the route
    // guards above), preserving its query state. Falls back to the browse
    // page only when there's no recorded in-app origin — a direct link,
    // a hard reload, or arriving from outside the app.
    goToCandidates() {
      if (this.originRoute) {
        this.$router.push(this.originRoute).catch(() => {})
      } else {
        this.$router.push({ name: 'candidates.list-page' })
      }
    },

    // Fetched through the authenticated axios instance (attaches the Bearer
    // token via interceptor) since resumeRequestUrl is gated — a plain link
    // would 401. On S3-backed storage the endpoint hands back a signed S3
    // URL as JSON (not a redirect it previously followed and read into a
    // blob), specifically because the S3 bucket has no CORS policy allowing
    // browser reads at all — confirmed directly, S3 answers a CORS
    // preflight-style request with 200 and no Access-Control-Allow-Origin
    // header, so the old fetch-as-blob approach always failed with a
    // generic Network Error, regardless of whether the file existed.
    // Showing that URL in the preview dialog's <iframe> is a plain navigation
    // too (CORS doesn't apply), so it works where a fetch would not.
    //
    // But on a non-S3 disk (local dev — see CandidateProfileController::
    // resume()'s own comment on why), the API can't sign a temporary URL at
    // all and instead streams the resume file itself as the response body,
    // not JSON. Request as a blob unconditionally and branch on the
    // response's actual Content-Type rather than assuming JSON, so both
    // shapes work: JSON branch reads resume_url out of it and previews that
    // URL; file branch previews the blob itself via an object URL.
    async viewResume() {
      if (!this.resumeRequestUrl) return
      this.isLoadingResume = true
      try {
        const response = await this.$api.get(this.resumeRequestUrl, {
          responseType: 'blob',
        })
        const contentType = response.headers['content-type'] || ''

        if (contentType.includes('application/json')) {
          const text = await response.data.text()
          const parsed = JSON.parse(text)
          const url = parsed && parsed.data && parsed.data.resume_url
          if (!url) throw new Error('No resume URL returned.')
          this.showResumePreview(url)
        } else {
          // The blob URL has to stay alive while the preview is open; it is
          // released when the preview closes (closeResumePreview).
          this.showResumePreview(URL.createObjectURL(response.data), true)
        }
      } catch (err) {
        this.onError(err)
      } finally {
        this.isLoadingResume = false
      }
    },

    showResumePreview(url, isObjectUrl = false) {
      this.releaseResumeObjectUrl()
      this.resumeObjectUrl = isObjectUrl ? url : null
      this.resumePreviewUrl = url
    },

    closeResumePreview() {
      this.resumePreviewUrl = ''
      this.releaseResumeObjectUrl()
    },

    releaseResumeObjectUrl() {
      if (this.resumeObjectUrl) URL.revokeObjectURL(this.resumeObjectUrl)
      this.resumeObjectUrl = null
    },

    openResumeRequestModal() {
      this.isRequestingResume = true
    },

    openWorkforceInvitationModal() {
      this.isInvitingWorkforce = true
    },

    onResumeRequestSent() {
      this.isRequestingResume = false
      this.alertMsg = 'Resume request sent.'
      this.alertType = 'success'
      this.dismissCountDown = 5
      this.fetchCandidate()
    },

    onWorkforceInvitationSent() {
      this.isInvitingWorkforce = false
      this.alertMsg = 'Workforce invitation sent.'
      this.alertType = 'success'
      this.dismissCountDown = 5
      this.fetchCandidate()
    },

    onAddedToPool() {
      this.alertMsg = 'Candidate added to pool.'
      this.alertType = 'success'
      this.dismissCountDown = 5
    },

    onError(err) {
      this.alertMsg =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'Something went wrong.'
      this.alertType = 'danger'
      this.dismissCountDown = 5
    },
  },
}
</script>

<style lang="scss" scoped>
// Mobile-first spacing. The old markup stacked Bootstrap utilities (py-5 on
// this container, mt-5 on the inner one, pt-5 + mt-5 on the breadcrumb, on
// top of this class's own 2rem padding-top) that add up fine on a tall
// desktop viewport but ate roughly a third of a 320px-tall phone screen
// before any real content appeared. Those utility classes are gone from the
// template now (see candidate-view-inner / candidate-view-breadcrumb below)
// — every one of their totals is preserved exactly at 768px+, just
// compressed on small screens instead of stacking unchanged.
.candidate-profile-view {
  padding-top: 1rem;
  padding-bottom: 1.5rem;

  @media (min-width: 768px) {
    // 2rem (this class, unchanged) + 3rem (old py-5 top)
    padding-top: 5rem;
    // old py-5 bottom
    padding-bottom: 3rem;
  }
}

.candidate-view-inner {
  @media (min-width: 768px) {
    margin-top: 3rem; // old mt-5
  }
}

.candidate-view-breadcrumb {
  padding-top: 0.25rem;

  @media (min-width: 768px) {
    padding-top: 3rem; // old pt-5
    margin-top: 3rem; // old mt-5
  }
}

/deep/ .candidate-details__body {
  padding: 1.25rem;

  @media (min-width: 768px) {
    padding: 3.5rem;
  }
}

// Was a b-row/b-col pair — bare .col never stacks (it's equal-width at
// every breakpoint, not just md+), so on narrow screens the name+star and
// the "Back to Candidates" button fought for the same horizontal line
// instead of stacking, producing the clumped/overlapping look. Flex column
// with an explicit gap on mobile, back to a row at 768px+ (this file's own
// breakpoint elsewhere) fixes it directly instead of fighting the grid.
.profile-header-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;

  &__identity {
    display: flex;
    align-items: center;
    justify-content: space-between; // push the star to the far right on mobile
    gap: 0.75rem;
    width: 100%;
    min-width: 0; // let the name shrink/wrap instead of pushing the star out
    max-width: 100%;
  }

  &__name-block {
    min-width: 0;

    h2 {
      overflow-wrap: anywhere;
    }
  }

  &__back-btn {
    align-self: stretch; // full-width pill on mobile instead of a small boxy button
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;

    &__identity {
      width: auto;
      justify-content: flex-start;
    }

    &__back-btn {
      align-self: auto;
    }
  }
}

.candidate-details__resume-gone {
  display: inline-flex;
  align-items: center;
}

.candidate-details__header-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1.25rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e1e6ea;
}

// The 4 action controls (Resume Request, Workforce Invite, Add to Pool, View
// Resume) used to be a bare flex-wrap row. Their labels vary a lot in width
// ("Resume Accepted" vs "Token Pending" vs "Add to Pool"), so on narrow
// screens they wrapped unevenly — one alone on a line, two paired on the
// next, the last alone again — reading as random instead of a layout. A
// fixed 2-column grid on mobile gives every control the same cell width so
// they line up as a tidy 2x2 block; back to a natural-width flex row at
// 768px+ where there's room for all 4 side by side.
.candidate-details__actions {
  display: grid;
  // minmax(0, 1fr), not a bare 1fr: a bare fr track won't shrink below its
  // content's min-content width, so the wider labels ("Resume Accepted")
  // pushed that column past the even 50% split and overflowed the card's
  // right padding — flush on the right, gapped on the left. minmax(0, 1fr)
  // lets both columns actually share the width evenly.
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  width: 100%;

  > * {
    width: 100%;
  }

  /deep/ .resume-request-btn,
  /deep/ .workforce-invite-btn,
  /deep/ .add-to-pool-dropdown > .dropdown-toggle,
  > .btn {
    width: 100%;
  }

  @media (min-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: auto;
    // Now the only item in the header row, so keep it on the right.
    margin-left: auto;

    > * {
      width: auto;
    }

    /deep/ .resume-request-btn,
    /deep/ .workforce-invite-btn,
    /deep/ .add-to-pool-dropdown > .dropdown-toggle,
    > .btn {
      width: auto;
    }
  }
}
</style>
